/**
 * Thread View Component
 * Displays a message thread (parent + replies) in the RightPanel
 */

import React, { useState } from 'react';
import { Bell, BellOff } from 'lucide-react';
import { useThread } from '@/hooks/useThread';
import { useAppStore } from '@/store/app.store';
import {
  useThreadSubscription,
  useToggleThreadSubscription,
} from '@/hooks/useThreadSubscription';
import MessageItem from './MessageItem';
import MessageInput from './MessageInput';
import type { Message } from '@/types/api.types';

export const ThreadView: React.FC = () => {
  const { activeThreadMessageId, setActiveThreadMessage } = useAppStore();
  const { data: thread, isLoading, error } = useThread(activeThreadMessageId);
  const { data: isSubscribed } = useThreadSubscription(activeThreadMessageId);
  const toggleSubscription = useToggleThreadSubscription();
  const [replyToMessage, setReplyToMessage] = useState<Message | null>(null);

  const handleToggleSubscription = () => {
    if (!activeThreadMessageId) return;
    toggleSubscription.mutate({
      messageId: activeThreadMessageId,
      subscribed: !!isSubscribed,
    });
  };

  if (!activeThreadMessageId) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-gray-500 dark:text-dark-text-muted">
        <svg className="w-12 h-12 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
          />
        </svg>
        <p className="text-sm">Изберете съобщение за преглед на отговори</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-32">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 text-center text-red-600 dark:text-red-400">
        <p>Грешка при зареждане на нишката</p>
      </div>
    );
  }

  if (!thread || thread.length === 0) {
    return (
      <div className="p-4 text-center text-gray-500 dark:text-dark-text-muted">
        <p>Няма намерени отговори</p>
      </div>
    );
  }

  const parentMessage = thread[0];
  const replies = thread.slice(1);

  const handleCancelReply = () => {
    setReplyToMessage(null);
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-200 dark:border-dark-border-primary flex items-center justify-between bg-white dark:bg-dark-bg-secondary sticky top-0 z-10">
        <div>
          <h3 className="font-semibold text-gray-900 dark:text-dark-text-primary">Нишка</h3>
          <p className="text-xs text-gray-500 dark:text-dark-text-muted">
            {replies.length} {replies.length === 1 ? 'отговор' : 'отговора'}
          </p>
        </div>
        <div className="flex items-center gap-1">
          {/* Follow / unfollow thread (feature #62) */}
          <button
            onClick={handleToggleSubscription}
            disabled={toggleSubscription.isPending}
            className={`flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs font-medium transition-colors disabled:opacity-50 ${
              isSubscribed
                ? 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-500/10 hover:bg-blue-100 dark:hover:bg-blue-500/20'
                : 'text-gray-500 dark:text-dark-text-muted hover:bg-gray-100 dark:hover:bg-dark-bg-hover'
            }`}
            title={isSubscribed ? 'Спри да следиш темата' : 'Следвай темата'}
          >
            {isSubscribed ? <BellOff className="w-4 h-4" /> : <Bell className="w-4 h-4" />}
            <span className="hidden sm:inline">{isSubscribed ? 'Следваш' : 'Следвай'}</span>
          </button>
          <button
            onClick={() => setActiveThreadMessage(null)}
            className="text-gray-400 dark:text-dark-text-muted hover:text-gray-600 dark:hover:text-dark-text-secondary transition-colors p-1"
            title="Затвори нишката"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      {/* Thread Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* Parent Message */}
        <div className="pb-4 border-b border-gray-200 dark:border-dark-border-secondary">
          <div className="mb-2 text-xs font-semibold text-gray-500 dark:text-dark-text-muted uppercase">
            Оригинално съобщение
          </div>
          <MessageItem message={parentMessage} />
        </div>

        {/* Replies */}
        {replies.length > 0 && (
          <div>
            <div className="mb-2 text-xs font-semibold text-gray-500 dark:text-dark-text-muted uppercase">
              Отговори
            </div>
            <div className="space-y-2">
              {replies.map((reply) => (
                <MessageItem key={reply.id} message={reply} />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Reply Input */}
      <div className="border-t border-gray-200 dark:border-dark-border-primary bg-white dark:bg-dark-bg-secondary p-4">
        <MessageInput
          replyToMessage={replyToMessage || parentMessage}
          onCancelReply={handleCancelReply}
        />
      </div>
    </div>
  );
};
